if (!customElements.get('store-stock-opener')) {
  class StoreStockOpener extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.ref = this.dataset.productSku;
      this.productId = this.dataset.productId;
      this.opener = this.querySelector('modal-opener');
      this.setSecondaryLink();
    }

    setSecondaryLink() {
      this.links = document.querySelectorAll('a[href="#store-stock"]');

      if (this.links.length <= 0) return;

      this.links.forEach((link) => {
        link.classList.remove('hidden');
        link.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.opener) {
            this.opener.openModal(link);
          }
        });
      });
    }
  }

  customElements.define('store-stock-opener', StoreStockOpener);
}

if (!customElements.get('store-stock')) {
  customElements.define(
    'store-stock',
    class StoreStock extends ModalDialog {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      async init() {
        // Data attributes
        this.originalStoreHTML = new Map();
        this.enableSearchHighlight = this.dataset.enableSearchHighlight === 'true';
        this.showStockColors = this.dataset.showStockColors === 'true';
        this.hideUnavailableStores = this.dataset.hideUnavailableStores === 'true';
        this.forceAvailable = this.dataset.forceAvailable === 'true';
        this.sortByAvailability = this.dataset.sortByAvailability === 'true';
        this.ref = this.dataset.productSku;
        this.productId = this.dataset.productId;
        this.country = this.dataset.country;
        this.countryFilter = 'MX';
        this.countryNameFilter = 'MEXICO';

        // DOM references
        this.storeList = this.querySelector('.store-stock-list');
        this.searchWrapper = this.querySelector('.store-stock__search');
        this.searchInput = this.querySelector('input[name="store_stock_search"]');
        this.searchButton = this.querySelector('.store-stock__search__button');
        this.emptyState = this.querySelector('[data-search-empty-state]');
        this.noStoresState = this.querySelector('[data-no-stores-state]');
        this.loadingState = this.querySelector('[data-loading-state]');
        this.errorState = this.querySelector('[data-error-state]');
        this.template = this.querySelector('[data-store-template]');

        this.setSecondaryLink();
        await this.getStock();
      }

      reset() {
        // Clear store list items
        if (this.storeList) {
          const storeItems = this.storeList.querySelectorAll('.store-stock-list__item');
          storeItems.forEach((item) => item.remove());
        }

        // Clear original HTML cache
        this.originalStoreHTML?.clear();

        // Remove search event listeners
        if (this.searchInput && this.debouncedSearch) {
          this.searchInput.removeEventListener('input', this.debouncedSearch);
        }
        if (this.searchButton && this.boundSearchClick) {
          this.searchButton.removeEventListener('click', this.boundSearchClick);
        }

        // Reset search input value
        if (this.searchInput) {
          this.searchInput.value = '';
        }

        // Hide all state elements
        this.hideElement(this.emptyState);
        this.hideElement(this.noStoresState);
        this.hideElement(this.errorState);
        this.hideElement(this.searchWrapper);

        // Show loading state for re-initialization
        this.showElement(this.loadingState);

        // Clear references
        this.debouncedSearch = null;
        this.boundSearchClick = null;
        this.storeIds = null;

        // Re-initialize
        this.init();
      }

      setSecondaryLink() {
        this.links = document.querySelectorAll('a[href="#store-stock"]');

        if (this.links.length <= 0) return;

        this.links.forEach((link) => {
          link.classList.remove('hidden');
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.show(link);
          });
        });
      }

      setUpSearchInput() {
        this.originalStoreHTML.clear();

        if (!this.searchInput) return;

        this.debouncedSearch = debounce(this.filterStores.bind(this), 300);
        this.searchInput.addEventListener('input', this.debouncedSearch);

        if (this.searchButton) {
          this.boundSearchClick = (e) => {
            e.preventDefault();
            this.filterStores();
          };
          this.searchButton.addEventListener('click', this.boundSearchClick);
        }
      }

      showElement(element) {
        if (!element) return;
        element.classList.remove('hidden');
        element.setAttribute('aria-hidden', 'false');
      }

      hideElement(element) {
        if (!element) return;
        element.classList.add('hidden');
        element.setAttribute('aria-hidden', 'true');
      }

      normalizeString(str) {
        return str
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
      }

      normalizeStoreCode(code) {
        if (!code) return code;
        const str = String(code);
        return /^\d$/.test(str) ? str.padStart(2, '0') : str;
      }

      isStoreInCountry(store) {
        const normalize = (value) => this.normalizeString(String(value ?? '')).toUpperCase();
        return (
          normalize(store.store_country?.code) === this.countryFilter ||
          normalize(store.store_country?.name) === this.countryNameFilter
        );
      }

      getSearchableText(storeItem) {
        const name = storeItem.querySelector('.store-stock-list__item__title')?.textContent || '';
        const addressText = storeItem.querySelector('.store-stock-address')?.textContent || '';
        return this.normalizeString(`${name} ${addressText}`);
      }

      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }

      highlightText(element, searchTerm) {
        if (!searchTerm || searchTerm.length === 0) return;

        const normalizedSearch = this.normalizeString(searchTerm);
        const escapedSearch = this.escapeRegExp(normalizedSearch);
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
        const nodesToReplace = [];

        while (walker.nextNode()) {
          const node = walker.currentNode;
          const normalizedText = this.normalizeString(node.textContent);

          if (normalizedText.includes(normalizedSearch)) {
            nodesToReplace.push(node);
          }
        }

        nodesToReplace.forEach((node) => {
          const text = node.textContent;
          const normalizedText = this.normalizeString(text);
          const parts = [];
          let lastIndex = 0;

          for (let i = 0; i < text.length; i++) {
            const remainingNormalized = normalizedText.substring(i);

            if (remainingNormalized.startsWith(normalizedSearch)) {
              if (i > lastIndex) {
                parts.push(document.createTextNode(text.substring(lastIndex, i)));
              }

              const matchLength = this.getOriginalMatchLength(text.substring(i), normalizedSearch);
              const matchText = text.substring(i, i + matchLength);
              const mark = document.createElement('mark');
              mark.className = 'store-stock-highlight';
              mark.textContent = matchText;
              parts.push(mark);

              i += matchLength - 1;
              lastIndex = i + 1;
            }
          }

          if (lastIndex < text.length) {
            parts.push(document.createTextNode(text.substring(lastIndex)));
          }

          if (parts.length > 0) {
            const fragment = document.createDocumentFragment();
            parts.forEach((part) => fragment.appendChild(part));
            node.parentNode.replaceChild(fragment, node);
          }
        });
      }

      getOriginalMatchLength(text, normalizedSearch) {
        let length = 0;
        let normalizedLength = 0;

        while (normalizedLength < normalizedSearch.length && length < text.length) {
          const char = text[length];
          const normalizedChar = this.normalizeString(char);
          normalizedLength += normalizedChar.length;
          length++;
        }

        return length;
      }

      filterStores() {
        if (!this.searchInput || !this.storeList) return;

        const searchTerm = this.normalizeString(this.searchInput.value);
        const storeItems = this.storeList.querySelectorAll('.store-stock-list__item');

        storeItems.forEach((item) => {
          if (!this.originalStoreHTML.has(item)) {
            this.originalStoreHTML.set(item, item.innerHTML);
          }
        });

        if (searchTerm === '') {
          storeItems.forEach((item) => {
            item.classList.remove('hidden');
            if (this.originalStoreHTML.has(item)) {
              item.innerHTML = this.originalStoreHTML.get(item);
            }
          });
          this.hideElement(this.emptyState);
          return;
        }

        let visibleCount = 0;

        storeItems.forEach((item) => {
          if (this.originalStoreHTML.has(item)) {
            item.innerHTML = this.originalStoreHTML.get(item);
          }

          const searchableText = this.getSearchableText(item);

          if (searchableText.includes(searchTerm)) {
            item.classList.remove('hidden');
            visibleCount++;

            if (this.enableSearchHighlight) {
              const titleElement = item.querySelector('.store-stock-list__item__title');
              const addressElement = item.querySelector('.store-stock-address');

              if (titleElement) this.highlightText(titleElement, this.searchInput.value);
              if (addressElement) this.highlightText(addressElement, this.searchInput.value);
            }
          } else {
            item.classList.add('hidden');
          }
        });

        if (visibleCount === 0) {
          this.showElement(this.emptyState);
        } else {
          this.hideElement(this.emptyState);
        }
      }

      async fetchStoreMetaobjects(storeIds) {
        const query = `
          query getStoreMetaobjects($cursor: String) {
            metaobjects(type: "stores", first: 250, after: $cursor) {
              nodes {
                fields {
                  key
                  value
                  reference {
                    ... on Metaobject {
                      fields {
                        key
                        value
                      }
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        `;

        const allStores = [];
        let cursor = null;
        let hasNextPage = true;

        try {
          while (hasNextPage) {
            const response = await fetch(SUNRISE_STORE_METAOBJECT_API, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': SUNRISE_STORE_METAOBJECT_KEY,
              },
              body: JSON.stringify({
                query,
                variables: { cursor },
              }),
            });

            if (!response.ok) {
              throw new Error(`GraphQL Error: ${response.status}`);
            }

            const result = await response.json();

            if (result.errors) {
              throw new Error(result.errors[0].message);
            }

            const { nodes, pageInfo } = result.data?.metaobjects || {};

            // Parse nodes and add to allStores
            const stores = (nodes || []).map((node) => {
              const fields = {};
              node.fields.forEach((field) => {
                // Check if field has a reference (linked metaobject)
                if (field.reference?.fields) {
                  // Return the full referenced metaobject as an object
                  const refFields = {};
                  field.reference.fields.forEach((refField) => {
                    refFields[refField.key] = refField.value;
                  });
                  fields[field.key] = refFields;
                } else {
                  fields[field.key] = field.value;
                }
              });
              if (fields.code != null) fields.code = this.normalizeStoreCode(fields.code);
              return fields;
            });

            allStores.push(...stores);

            // Update pagination state
            hasNextPage = pageInfo?.hasNextPage || false;
            cursor = pageInfo?.endCursor || null;
          }

          return allStores.filter((store) => store.code != null);
        } catch (error) {
          console.error('[StoreStock] Metaobject fetch error:', error);
          throw error;
        }
      }

      async getStores(data) {
        this.storeIds = data.map(store => store?.idtienda);

        try {
          const stores = await this.fetchStoreMetaobjects(this.storeIds);

          this.hideElement(this.loadingState);

          if (stores.length === 0) {
            this.showElement(this.noStoresState);
            return;
          }

          this.hideElement(this.noStoresState);

          const filterByCountry = this.country === this.countryFilter;

          let storeItems = stores.reduce((acc, store) => {
            if (filterByCountry && !this.isStoreInCountry(store)) return acc;

            const stockData = data.find((item) => String(item?.idtienda) === String(store.code));

            // Build formatted address: [address], [city], [province] [postal code]
            const province = store.store_province || store.provincia;
            const provinceName = typeof province === 'object' ? province.name : province;
            const postalCode = store.postal_code || store.codigo_postal;
            const provincePostal = [provinceName, postalCode].filter(Boolean).join(' ');

            const addressParts = [
              store.address || store.direccion,
              store.city || store.ciudad,
              provincePostal,
            ].filter(Boolean);

            // Parse rich text JSON fields
            const schedule = this.parseRichText(store.schedule);
            const description = this.parseRichText(store.description);

            // Translate stock availability text
            const disponibilidad = stockData?.disponibilidad ?? 'No disponible';
            const isAvailable = disponibilidad !== 'No disponible';

            if (this.hideUnavailableStores && !isAvailable) return acc;

            const translated = this.translateStockAvailability(disponibilidad);
            const availability = (this.forceAvailable && isAvailable)
              ? { text: window.storeStrings?.stockAvailable, level: this.showStockColors ? translated.level : null }
              : translated;

            acc.push({
              name: store.name || store.nombre || '',
              address: addressParts.join(', '),
              phone: store.phone || store.telefono || '',
              email: store.email || '',
              schedule: schedule,
              description: description,
              available: isAvailable,
              availabilityText: availability.text,
              stockLevel: availability.level,
            });

            return acc;
          }, []);

          if (this.sortByAvailability) {
            storeItems.sort((a, b) => b.available - a.available);
          }

          if (storeItems.length === 0) {
            this.hideElement(this.storeList);
            this.showElement(this.noStoresState);
            return;
          }

          this.showElement(this.storeList);
          storeItems.forEach((item) => this.renderStoreItem(item));

          // Set up search after items are added
          this.setUpSearchInput();
          this.showElement(this.searchWrapper);

        } catch (error) {
          this.hideElement(this.loadingState);
          this.showElement(this.errorState);
        }
      }

      parseRichText(jsonString) {
        if (!jsonString) return '';
        try {
          const parsed = JSON.parse(jsonString);
          const extractHtml = (node) => {
            if (node.type === 'text') {
              // Escape HTML entities for safety
              const text = node.value || '';
              return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            }
            if (node.children) {
              const content = node.children.map(extractHtml).join('');
              // Wrap paragraphs and add line breaks between them
              if (node.type === 'paragraph') {
                return `<p>${content}</p>`;
              }
              return content;
            }
            return '';
          };
          return extractHtml(parsed);
        } catch {
          return jsonString;
        }
      }

      translateStockAvailability(stockText) {
        if (!stockText) return { text: '', level: null };

        const upperText = stockText.toUpperCase();

        const stockMap = {
          'STOCK BAJO': { translation: window.storeStrings?.stockLow, level: 'low' },
          'STOCK MEDIO': { translation: window.storeStrings?.stockMedium, level: 'medium' },
          'STOCK ALTO': { translation: window.storeStrings?.stockHigh, level: 'high' },
          'NO DISPONIBLE': { translation: window.storeStrings?.stockUnavailable, level: 'none' },
        };

        const stockInfo = stockMap[upperText];

        if (stockInfo) {
          const text = stockInfo.translation && !stockInfo.translation.includes('missing')
            ? stockInfo.translation
            : stockText;
          return { text, level: stockInfo.level };
        }

        return { text: stockText, level: null };
      }

      renderStoreItem(store) {
        if (!this.template || !this.storeList) return;

        const clone = this.template.content.cloneNode(true);

        const nameEl = clone.querySelector('[data-store-name]');
        const addressEl = clone.querySelector('[data-store-address]');
        const phoneEl = clone.querySelector('[data-store-phone]');
        const emailEl = clone.querySelector('[data-store-email]');
        const scheduleEl = clone.querySelector('[data-store-schedule]');
        const descriptionEl = clone.querySelector('[data-store-description]');
        const availabilityEl = clone.querySelector('[data-store-availability]');
        const availabilityTextEl = clone.querySelector('[data-availability-text]');

        if (nameEl) nameEl.textContent = store.name || '';
        if (addressEl) addressEl.textContent = store.address || '';
        if (phoneEl) phoneEl.textContent = store.phone || '';
        if (emailEl) emailEl.textContent = store.email || '';
        if (scheduleEl) scheduleEl.innerHTML = store.schedule || '';
        if (descriptionEl) descriptionEl.innerHTML = store.description || '';

        if (availabilityEl && store.available !== undefined) {
          availabilityEl.classList.add(store.available ? 'available' : 'unavailable');

          // Add stock level class for colored indicators
          if (this.showStockColors && store.stockLevel) {
            availabilityEl.classList.add(`stock-${store.stockLevel}`);
          }
        }

        if (availabilityTextEl) availabilityTextEl.textContent = store.availabilityText || '';

        this.storeList.appendChild(clone);
      }

      async getStock() {
        if (!this.ref) {
          console.log('[StoreStock] No Reference');
          this.hideElement(this.loadingState);
          this.showElement(this.errorState);
          return null;
        }

        const timeout = 5000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        const fetchUrl = `https://shopify-api.joseluisjoyerias.com/api/9b0c14a7-b3f5-4436-8aba-c369bce9aaf4/${this.ref}`;

        try {
          const response = await fetch(fetchUrl, {
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
          } else {
            const data = await response.json();
            this.getStores(data);
          }
        } catch (error) {
          console.error('Error:', error.message);
          this.hideElement(this.loadingState);
          this.showElement(this.errorState);
          return null;
        }
      }

      show(opener) {
        super.show(opener);
        if (this.searchInput) {
          this.searchInput.value = '';
          this.filterStores();
        }
      }
    }
  );
}
